/**
 * enrichImageCollection — builds the `taggedImages` payload that makes an
 * `enrichReference` run multimodal, shared by the single-ref editor and the
 * bulk enrich tool.
 *
 * The callable wants `{ url, tags }` per image: the primary shot carries the
 * synthetic `primary` tag and every gallery image carries whatever the curator
 * tagged it with (dial, crown, caseback, …), which is what lets the prompt route
 * a field to the image that actually shows it. An untagged gallery image is
 * still worth sending — the prompt treats it as supporting evidence — so `tags`
 * is allowed to be empty, and only a missing URL drops an image.
 *
 * Type-only imports, and the storage resolver is injected rather than imported,
 * so this stays unit-testable without booting the Firebase app (same arrangement
 * as calibreResolution.ts).
 */

import type { ImageInfo } from '../types';

export interface TaggedEnrichImage {
  url: string;
  tags: string[];
}

/** Synthetic tag for the hero shot — the callable's top-priority image. */
export const PRIMARY_IMAGE_TAG = 'primary';

/**
 * Images sent per reference. Matches the callable's own default cap, so the
 * client-side estimate below and the server's actual spend agree; over-cap
 * images are dropped server-side (lowest priority first) and logged.
 */
export const MAX_ENRICH_IMAGES = 5;

/**
 * Images a VARIANT reference sends: one — its main shot. Variant mode asks only
 * for the six variant-level fields (dial colour / finish / style, case and strap
 * material, calibre), which the front-on main image answers; the caseback,
 * profile and detail shots a full enrichment uses for thickness, bezel or lume
 * add tokens and nothing for those six.
 */
export const VARIANT_MAX_ENRICH_IMAGES = 1;

/** Marginal cost of one image on an enrich call. */
export const ENRICH_IMAGE_COST_PER_IMAGE = 0.005;

/**
 * Gallery tags whose images are never worth an enrich slot.
 *
 * A box shot, a packet of papers and a wrist shot show packaging, paperwork and
 * a forearm — none of them carry a dial, a caseback, a bezel or a clasp, so
 * there is no attribute for the prompt to read off them. They still cost
 * {@link ENRICH_IMAGE_COST_PER_IMAGE} each and, worse, they consume slots under
 * the {@link MAX_ENRICH_IMAGES} cap that a dial or movement shot would have
 * used.
 *
 * Excluded at COLLECTION time, before the cap, so the images that survive are
 * the informative ones. The tags stay on the Firestore doc untouched — the
 * admin's gallery still shows and filters by them; this is purely about what
 * gets paid for on an AI call.
 */
export const ENRICH_EXCLUDED_TAGS: ReadonlySet<string> = new Set([
  'box',
  'wrist',
  'papers',
]);

/**
 * Is this gallery image worth sending to the AI?
 *
 * Untagged images pass — the prompt treats them as supporting evidence, and an
 * untagged shot is usually just an extra product photo. Only an explicit
 * {@link ENRICH_EXCLUDED_TAGS} tag disqualifies one. Tags are compared
 * case-insensitively; `sanitizeImageTags` already lower-cases everything it
 * writes, but a hand-authored doc need not have gone through it.
 */
export function isEnrichableImage(tags: readonly string[] | undefined): boolean {
  return !(tags ?? []).some((t) =>
    ENRICH_EXCLUDED_TAGS.has(String(t ?? '').trim().toLowerCase()),
  );
}

/**
 * Headline per-ref figure for a run at the full image cap — what a bulk batch
 * costs per ref in the worst case. Slightly under 5 × the per-image marginal
 * because the shared text prompt is paid once, not once per image.
 */
export const ENRICH_IMAGE_COST_PER_REF = 0.024;

/** The two image fields of a reference this module reads. */
export interface ReferenceImageSource {
  images?: ImageInfo[];
  galleryImages?: ImageInfo[];
}

/**
 * Zip already-resolved download URLs with their tags.
 *
 * Gallery URLs resolve asynchronously and a failed resolve leaves a null in the
 * URL array, so entries are matched to their ImageInfo BY INDEX and the nulls
 * dropped — filtering first and zipping after would misalign the tags and hand
 * the AI a caseback labelled "dial".
 *
 * This is the ONE place both callers converge — the ref editor zips its own
 * already-resolved URLs, the bulk tool comes through
 * {@link collectTaggedImagesForRef} — so the {@link ENRICH_EXCLUDED_TAGS} drop
 * lives here rather than in either caller.
 */
export function zipTaggedImages(
  primaryUrl: string | null | undefined,
  gallery: Array<{ url: string | null | undefined; tags?: string[] }>,
): TaggedEnrichImage[] {
  const out: TaggedEnrichImage[] = [];
  if (primaryUrl) out.push({ url: primaryUrl, tags: [PRIMARY_IMAGE_TAG] });
  for (const entry of gallery) {
    if (!entry?.url) continue;
    if (!isEnrichableImage(entry.tags)) continue;
    out.push({ url: entry.url, tags: entry.tags ?? [] });
  }
  return out;
}

/**
 * How many images this reference WOULD send, read straight off the doc — no
 * storage round-trips, so a whole batch can be counted for a cost estimate
 * before anything runs. Uncapped: the cap is applied by the estimator (and
 * enforced server-side), and the raw count is what tells an admin how many of
 * their images would be dropped.
 *
 * The primary is `images[0]` — the same one the editor previews — and only the
 * gallery contributes beyond it.
 *
 * Box / wrist / papers gallery shots are NOT counted: they are dropped before
 * the cap by {@link collectTaggedImagesForRef}, so counting them here would
 * over-quote the batch and mis-report images as "dropped over cap" that were
 * never candidates.
 */
export function countEnrichImageCandidates(ref: ReferenceImageSource): number {
  const primary = ref.images?.[0]?.storagePath?.trim() ? 1 : 0;
  const gallery = enrichableGallery(ref).length;
  return primary + gallery;
}

/**
 * Stable identity for the images an enrich run WOULD send, read off the
 * document with no storage round-trips.
 *
 * For cache keys: an enrichment result depends on which images were sent, but
 * resolving them to download URLs is exactly the expensive work a cache hit
 * exists to skip — and the URLs are signed and time-varying, so they would key
 * differently on every call anyway. Storage paths plus tags are what actually
 * determine the answer, and they come straight off the doc.
 *
 * Derived from the same `enrichableGallery` the send path uses, so a change to
 * what gets sent can't silently leave the key describing the old set.
 */
export function enrichImageIdentity(ref: ReferenceImageSource): string[] {
  const out: string[] = [];
  const primary = ref.images?.[0]?.storagePath?.trim();
  if (primary) out.push(`${PRIMARY_IMAGE_TAG}:${primary}`);
  for (const img of enrichableGallery(ref)) {
    out.push(`${(img.tags ?? []).join('|')}:${img.storagePath?.trim() ?? ''}`);
  }
  return out;
}

/**
 * The gallery images an enrich run would actually send: a readable storage path
 * AND no disqualifying tag. One definition, shared by the cost estimate and the
 * send path, so the two can't drift.
 */
function enrichableGallery(ref: ReferenceImageSource): ImageInfo[] {
  return (ref.galleryImages ?? []).filter(
    (img) => img?.storagePath?.trim() && isEnrichableImage(img.tags),
  );
}

export interface EnrichImageEstimate {
  /** Refs in the batch that have at least one image. */
  refsWithImages: number;
  /** Images that would actually be sent, after the per-ref cap. */
  images: number;
  /** Images present on the refs but over the per-ref cap, so never sent. */
  droppedOverCap: number;
  /** Estimated marginal image spend for the batch, in dollars. */
  cost: number;
}

/**
 * Cost estimate for image-enriching a batch, from the docs alone. Refs with no
 * images cost nothing extra — they run text-only exactly as they do today.
 */
export function estimateEnrichImageCost<R extends ReferenceImageSource>(
  refs: R[],
  maxImages: number = MAX_ENRICH_IMAGES,
  /** Per-ref cap override — variants send one image (VARIANT_MAX_ENRICH_IMAGES). */
  capFor?: (ref: R) => number,
): EnrichImageEstimate {
  let refsWithImages = 0;
  let images = 0;
  let droppedOverCap = 0;
  for (const ref of refs) {
    const candidates = countEnrichImageCandidates(ref);
    if (candidates === 0) continue;
    const cap = capFor ? capFor(ref) : maxImages;
    refsWithImages += 1;
    images += Math.min(candidates, cap);
    // A variant's single image is by design, not an overflow worth warning about.
    if (cap === maxImages) droppedOverCap += Math.max(0, candidates - maxImages);
  }
  return {
    refsWithImages,
    images,
    droppedOverCap,
    cost: images * ENRICH_IMAGE_COST_PER_IMAGE,
  };
}

/**
 * Resolve a reference's storage paths to download URLs and return them in the
 * shape `enrichReference` wants.
 *
 * `resolveUrl` is injected (pass `getImageUrl`) to keep this module free of the
 * Firebase app. Resolution runs in parallel and a failure drops that one image
 * rather than the run — a ref whose images are all unreadable simply enriches on
 * text, which is what the tool did before images existed.
 *
 * Box / wrist / papers gallery shots are dropped here, BEFORE the per-ref cap,
 * so they can't crowd out a dial or movement shot (see
 * {@link ENRICH_EXCLUDED_TAGS}). The primary is exempt: it is the hero the whole
 * record is keyed on, its own tags are replaced by the synthetic
 * {@link PRIMARY_IMAGE_TAG} anyway, and dropping it would leave a ref sending a
 * gallery with no main shot.
 */
export async function collectTaggedImagesForRef(
  ref: ReferenceImageSource,
  resolveUrl: (storagePath: string) => Promise<string>,
): Promise<TaggedEnrichImage[]> {
  const primaryPath = ref.images?.[0]?.storagePath?.trim();
  const gallery = enrichableGallery(ref);
  if (!primaryPath && gallery.length === 0) return [];

  const safeResolve = (path: string) => resolveUrl(path).catch(() => null);
  const [primaryUrl, galleryUrls] = await Promise.all([
    primaryPath ? safeResolve(primaryPath) : Promise.resolve(null),
    Promise.all(gallery.map((img) => safeResolve(img.storagePath))),
  ]);

  return zipTaggedImages(
    primaryUrl,
    galleryUrls.map((url, i) => ({ url, tags: gallery[i]?.tags })),
  );
}
